import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRightIcon, CheckIcon } from 'lucide-react';
import { useInView, useReducedMotion } from 'framer-motion';
import { ButtonLink } from '../ButtonLink';
import { Container } from '../Container';
import { Reveal } from '../Reveal';
import { Section } from '../Section';
import { PageCountSlider } from './PageCountSlider';
import { pricingTiers } from '../../data/pricing';
function parsePrice(price: string) {
  const match = price.match(/^([^\d]*?)(₹)(\s*)([\d,]+)$/);
  if (!match) return null;
  const [, prefix, symbol, spacer, digits] = match;
  return {
    prefix,
    symbol,
    spacer,
    value: parseInt(digits.replace(/,/g, ''), 10)
  };
}
function CountUpPrice({
  price
}: {
  price: string;
}) {
  const parsed = useMemo(() => parsePrice(price), [price]);
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-64px'
  });
  const prefersReducedMotion = useReducedMotion();
  const [displayValue, setDisplayValue] = useState(0);
  useEffect(() => {
    if (!parsed) return;
    if (!isInView || prefersReducedMotion) {
      setDisplayValue(parsed.value);
      return;
    }
    let frame: number;
    let start: number | null = null;
    const duration = 900;
    const step = (timestamp: number) => {
      if (start === null) start = timestamp;
      const progress = Math.min((timestamp - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayValue(Math.round(eased * parsed.value));
      if (progress < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [isInView, prefersReducedMotion, parsed]);
  if (!parsed) {
    return <span ref={ref} data-id="element-522" data-mp="1go0dpfgi9r7k">{price}</span>;
  }
  return <span ref={ref} data-id="element-523" data-mp="2n5f5hw81bpmr">
      {parsed.prefix}
      {parsed.symbol}
      {parsed.spacer}
      {displayValue.toLocaleString('en-IN')}
    </span>;
}
export function PricingTeaser() {
  const defaultIndex = Math.max(pricingTiers.findIndex(tier => tier.highlighted), 0);
  const [activeIndex, setActiveIndex] = useState(defaultIndex);
  const stepLabels = pricingTiers.map(tier => tier.pageRange);
  return <Section tone="surface" labelledBy="pricing-title" data-id="element-524" data-mp="1205awim70k8u">
      <Container data-id="element-525" data-mp="x2mygi8yvmw3">
        <Reveal className="flex flex-col justify-between gap-6 md:flex-row md:items-end" data-id="element-526" data-mp="x23rtxorokxd">
          <div data-id="element-527" data-mp="9c9fdh8d0ydn">
            <h2 id="pricing-title" className="max-w-[16ch] text-[40px] leading-[1.05] tracking-[-0.03em] text-ink lg:text-[56px]" data-id="element-528" data-mp="9dub3a6eund3">
              Fixed prices, agreed before we start.
            </h2>
            <p className="mt-6 max-w-[48ch] text-body text-muted" data-id="element-529" data-mp="j4oxcejnpmyj">
              No hourly billing, no scope surprises. If the work changes, we re-quote in writing
              before anyone does anything.
            </p>
          </div>
          <Link to="/pricing" className="group inline-flex shrink-0 items-center gap-2 text-small text-muted transition-colors duration-150 ease-out hover:text-ink" data-id="element-530" data-mp="3ht49a776j8fl">
            Full pricing
            <ArrowUpRightIcon size={16} aria-hidden="true" data-id="element-531" data-mp="222bocuwbp4v6" />
          </Link>
        </Reveal>

        <Reveal delay={0.06} className="mt-16 lg:mt-20" data-id="element-532" data-mp="1oawuft0vnj9c">
          <p className="text-small text-muted" data-id="element-533" data-mp="1sbgsqzx6ssx2">How many pages do you need?</p>
          <div className="mt-6" data-id="element-534" data-mp="1s9e5gnnf5dw1">
            <PageCountSlider steps={stepLabels} activeIndex={activeIndex} onChange={setActiveIndex} data-id="element-535" data-mp="2g1b5zilf4jhu" />
          </div>
        </Reveal>

        <ul className="mt-14 grid items-stretch gap-6 lg:mt-16 lg:grid-cols-3" data-id="element-536" data-mp="2kyuudbbtycni">
          {pricingTiers.map((tier, index) => {
          const isMatched = index === activeIndex;
          return <Reveal as="li" key={tier.name} delay={index * 0.06} className={`h-full ${tier.highlighted ? 'lg:relative lg:z-10 lg:scale-[1.04]' : ''}`} data-id="element-537" data-mp="2by54eo993ghp" data-mpi={index}>
                <div className={`relative flex h-full flex-col rounded-card p-10 transition-[border-color,opacity] duration-300 ease-editorial ${isMatched ? 'border-2 border-ink opacity-100' : 'border border-hairline opacity-[0.55]'} ${tier.highlighted ? 'bg-canvas' : 'bg-canvas'}`} data-id="element-538" data-mp="10hy9w43vwgto">
                  {tier.highlighted && <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 rounded-full bg-ink px-3 py-1 text-[12px] font-medium text-white" data-id="element-539" data-mp="120q71697twpa">
                      Most chosen
                    </span>}

                  <h3 className="text-small font-semibold text-ink" data-id="element-540" data-mp="1prm0s8x8f1z6">{tier.name}</h3>
                  <span className="mt-3 inline-flex w-fit items-center rounded-full border border-hairline px-3 py-1 text-small text-muted" data-id="element-541" data-mp="1ktasw3qhz1jl">
                    {tier.pageRange}
                  </span>

                  <p className="mt-6 text-[32px] leading-none tracking-[-0.03em] text-ink lg:text-[40px]" data-id="element-542" data-mp="9e5gg80kdoms">
                    <CountUpPrice price={tier.price} data-id="element-543" data-mp="2l1qygpazjdqq" />
                  </p>
                  <p className="mt-3 text-small text-muted" data-id="element-544" data-mp="2q0bro4rtkx5o">{tier.cadence}</p>
                  <p className="mt-6 max-w-[34ch] text-body text-ink" data-id="element-545" data-mp="2py7qhulb2c93">{tier.summary}</p>
                  <p className="mt-6 max-w-[32ch] text-small text-muted" data-id="element-546" data-mp="2u0m6m6xlaxwo">Best for: {tier.bestFor}</p>

                  <ul className="mt-8 space-y-3 border-t border-hairline pt-8" data-id="element-547" data-mp="2ytx61n02ocek">
                    {tier.includedItems.map(item => <li key={item} className="flex items-start gap-2.5" data-id="element-548" data-mp="38qjpq8gnwxf0">
                        <CheckIcon size={16} aria-hidden="true" className="mt-0.5 shrink-0 text-ink" data-id="element-549" data-mp="38smophzsm0vv" />
                        <span className="text-small leading-5 text-ink" data-id="element-550" data-mp="3do22k9xsvwsa">{item}</span>
                      </li>)}
                  </ul>

                  <ButtonLink to="/contact" variant={tier.ctaVariant} className="mt-8 w-full" data-id="element-551" data-mp="1jd6r8zv8e3lt">
                    {tier.ctaLabel}
                  </ButtonLink>
                </div>
              </Reveal>;
        })}
        </ul>

        <p className="mt-10 text-center text-[13px] text-muted" data-id="element-552" data-mp="2lr4ba9v7hfy1">
          All plans include design, build, CMS, SEO basics and 30 days of post-launch support.
          Integrations quoted separately from ₹ 5,000.
        </p>
        <p className="mt-3 text-center text-[13px]" data-id="element-553" data-mp="39n8o1mw4k0dn">
          <Link to="/pricing" className="text-accent underline decoration-accent/30 underline-offset-4 transition-colors duration-150 ease-out hover:decoration-accent" data-id="element-554" data-mp="39lxjhkuuuolh">
            See add-ons and integrations
          </Link>
        </p>
      </Container>
    </Section>;
}